import { Button } from "@/components/ui/button";
import {
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useAppForm } from "../../hooks/useForm";
import { z } from "zod";
import type { Empresa } from "../../@types/Api_Extracted";
import { Label } from "../ui/label";
import { useApi } from "../../hooks/useApi";
import { toast } from "react-toastify";

interface EmpresaDialogProps {
	onSuccess: () => void;
	empresa: Empresa;
}
export function DialogEditEmpresa(props: EmpresaDialogProps) {
	const api = useApi();
	const mutation = api.useMutation("put", "/v1/Admin/Empresas/{idEmpresa}");
	const form = useAppForm({
		defaultValues: {
			...props.empresa,
			password: "",
		} satisfies FormSchema as FormSchema,
		validators: {
			onChange: FormSchema,
		},
		onSubmit: async (data) => {
			try {
				await mutation.mutateAsync({
					params: {
						path: { idEmpresa: props.empresa.idUsuario },
					},
					body: data.value,
				});
				toast("Datos guardados.", { type: "success" });
				props.onSuccess();
			} catch (err) {
				toast("Error al guardar los datos.", { type: "error" });
			}
		},
	});

	return (
		<DialogContent
			className="sm:max-w-[425px]"
			description="Modal para editar una empresa"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit(e);
				}}
				className="space-y-8"
			>
				<DialogHeader>
					<DialogTitle>Editar Empresa</DialogTitle>
				</DialogHeader>
				{/* <SelectControl control={form.control} name="estado" label="Estado" /> */}

				<form.AppField
					name="estado"
					children={(field) => (
						<div>
							<Label>Estado</Label>
							<field.SelectField
								options={{
									PENDING: localizedStatus["PENDING"],
									ACTIVE: localizedStatus["ACTIVE"],
									INACTIVE: localizedStatus["INACTIVE"],
								}}
							/>
						</div>
					)}
				/>

				<form.AppField
					name="razonSocial"
					children={(field) => (
						<div>
							<Label>Razón Social</Label>
							<field.TextField
								placeholder="Razón Social"
								type="text"
								autoComplete="organization-name"
							/>
						</div>
					)}
				/>
				<form.AppField
					name="username"
					children={(field) => (
						<div>
							<Label>Correo Electrónico</Label>
							<field.TextField
								placeholder="Correo Electrónico"
								type="email"
								autoComplete="email"
							/>
						</div>
					)}
				/>
				<form.AppField
					name="password"
					children={(field) => (
						<div>
							<Label>Contraseña</Label>
							<field.TextField
								placeholder="Contraseña"
								type="password"
								autoComplete="new-password"
							/>
						</div>
					)}
				/>
				<form.AppField
					name="personaContacto"
					children={(field) => (
						<div>
							<Label>Persona de contacto</Label>
							<field.TextField
								placeholder="Nombre"
								type="text"
								autoCapitalize="name"
							/>
						</div>
					)}
				/>
				<form.AppField
					name="telefonoContacto"
					children={(field) => (
						<div>
							<Label>Número de contacto</Label>
							<field.TextField
								placeholder="Teléfono"
								type="tel"
								autoComplete="tel"
							/>
						</div>
					)}
				/>
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" type="button">
							Cancelar
						</Button>
					</DialogClose>

					<Button type="submit">Save changes</Button>
				</DialogFooter>
			</form>
		</DialogContent>
	);
}

const FormSchema = z.object({
	idUsuario: z.number(),
	estado: z.enum(["PENDING", "ACTIVE", "INACTIVE"]),
	razonSocial: z
		.string({
			required_error: "La razón social es obligatoria.",
		})
		.min(2, {
			message: "La razón social debe tener al menos 2 caracteres.",
		}),
	username: z
		.string({
			required_error: "El correo electrónico es obligatorio.",
		})
		.email({
			message: "El correo electrónico debe ser válido.",
		})
		.transform((email) => email.toLowerCase()),
	password: z
		.string({
			required_error: "La contraseña es obligatoria.",
		})
		.regex(/(^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$)|^$/, {
			message:
				"La contraseña debe tener al menos 8 caracteres, una letra mayúscula, una letra minúscula y un número.",
		}),
	personaContacto: z
		.string({
			required_error: "El nombre de contacto es obligatorio.",
		})
		.min(2, {
			message: "El nombre de contacto debe tener al menos 2 caracteres.",
		}),
	telefonoContacto: z
		.string({
			required_error: "El número de contacto es obligatorio.",
		})
		.regex(/^(\+\d{1,3})?(\d{6,12})$/, {
			message:
				"El número de contacto debe de ser un número de teléfono válido.",
		}),
});
type FormSchema = z.infer<typeof FormSchema>;
const localizedStatus = {
	PENDING: "Pendiente",
	ACTIVE: "Activo",
	INACTIVE: "Inactivo",
};
